import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { SkeletonList } from "@/components/Skeletons";
import { Avatar } from "@/components/Avatar";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { relativeDate } from "@/lib/format";
import { toast } from "sonner";
import client, { apiError } from "@/lib/api";
import { Trash2, Download, Camera, ImagePlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
    ["limpiador", "Limpiador"], ["desmaquillante", "Desmaquillante"], ["tonico", "Tónico"],
    ["serum_vitc", "Sérum de vitamina C"], ["serum_hidratante", "Sérum hidratante"],
    ["exfoliante", "Exfoliante (AHA/BHA)"], ["retinol", "Retinol / retinoide"],
    ["contorno", "Contorno de ojos"], ["hidratante", "Hidratante"], ["aceite", "Aceite facial"],
    ["spf", "Protector solar"], ["mascarilla", "Mascarilla"], ["otro", "Otro"],
];
const SUIT = {
    adecuado: ["Te encaja", "border-line bg-background text-subtle"],
    con_cuidado: ["Con cuidado", "border-terracotta/30 bg-terracotta/10 text-terracotta"],
    prescindible: ["Quizá sobra", "border-destructive/30 bg-destructive/5 text-destructive"],
};

const MAX_PRODUCTS = 6;

function fileToResizedBase64(file, maxDim = 1280) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            let { width, height } = img;
            if (width > maxDim || height > maxDim) {
                const r = Math.min(maxDim / width, maxDim / height);
                width = Math.round(width * r);
                height = Math.round(height * r);
            }
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            canvas.getContext("2d").drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL("image/jpeg", 0.82).split(",")[1]);
        };
        img.onerror = reject;
        img.src = url;
    });
}

function PhotoField({ label, hint, value, onPick, testid }) {
    const camRef = useRef(null);
    const fileRef = useRef(null);
    return (
        <div className="rounded-lg border border-line bg-background p-4">
            <p className="text-[14px] font-medium text-ink">{label}</p>
            <p className="mt-0.5 text-[13px] text-subtle">{hint}</p>
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
                data-testid={`${testid}-camera`} onChange={onPick} />
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                data-testid={`${testid}-file`} onChange={onPick} />
            <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => camRef.current?.click()} data-testid={`${testid}-camera-btn`}>
                    <Camera size={15} className="mr-1.5" /> Hacer foto
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid={`${testid}-file-btn`}>
                    <ImagePlus size={15} className="mr-1.5" /> Seleccionar archivo
                </Button>
                {value && <span className="ml-1 text-[12px] font-medium text-terracotta" data-testid={`${testid}-ok`}>Foto lista ✓</span>}
            </div>
        </div>
    );
}

function AddDialog({ onAdded, total = 0, triggerLabel = "Añadir producto" }) {
    const token = useRef(crypto.randomUUID());
    const [open, setOpen] = useState(false);
    const [baseTotal, setBaseTotal] = useState(total);
    const [added, setAdded] = useState(0);
    const [stage, setStage] = useState("capture"); // capture | review | saved
    const [photoFront, setPhotoFront] = useState(null);
    const [photoIng, setPhotoIng] = useState(null);
    const [form, setForm] = useState({ name: "", brand: "", category: "otro" });
    const [ingredients, setIngredients] = useState([]);
    const [analyzing, setAnalyzing] = useState(false);
    const [saving, setSaving] = useState(false);

    const current = Math.min(baseTotal + added + 1, MAX_PRODUCTS);
    const reachedMax = baseTotal + added >= MAX_PRODUCTS;
    const full = total >= MAX_PRODUCTS;

    const resetProduct = () => {
        setStage("capture");
        setPhotoFront(null);
        setPhotoIng(null);
        setForm({ name: "", brand: "", category: "otro" });
        setIngredients([]);
        token.current = crypto.randomUUID();
    };

    const onOpenChange = (o) => {
        setOpen(o);
        if (o) { setBaseTotal(total); setAdded(0); resetProduct(); }
    };

    const pick = (setter) => async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (f.size > 8 * 1024 * 1024) return toast.error("La imagen es muy grande. Prueba con una menor de 8 MB.");
        try { setter(await fileToResizedBase64(f)); }
        catch { toast.error("No pudimos leer esa imagen. Prueba con otra."); }
    };

    const analyze = async () => {
        if (!photoFront || !photoIng) { toast.error("Sube las dos fotos del mismo producto."); return; }
        setAnalyzing(true);
        try {
            const { data } = await client.post("/products/identify", { photo_front: photoFront, photo_ingredients: photoIng });
            setForm({ name: data.name || "", brand: data.brand || "", category: data.category || "otro" });
            setIngredients(data.ingredients || []);
            setStage("review");
            if (!data.name && !data.brand) toast("No hemos podido leerlo del todo. Revísalo y corrígelo abajo.");
        } catch (err) {
            setForm({ name: "", brand: "", category: "otro" });
            setIngredients([]);
            setStage("review");
            toast("No hemos podido analizar las fotos. Completa los datos a mano.");
        } finally { setAnalyzing(false); }
    };

    const save = async () => {
        if (!form.name.trim()) { toast.error("El producto necesita un nombre. Corrígelo abajo."); return; }
        setSaving(true);
        try {
            const { data } = await client.post("/products", {
                name: form.name.trim(), brand: form.brand.trim(), category: form.category,
                ingredients, photo: photoFront, client_token: token.current,
            });
            toast.success(`"${data.name}" guardado en tu neceser.`);
            onAdded(data);
            setAdded((n) => n + 1);
            setStage("saved");
        } catch (err) { toast.error(apiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <Button data-testid="product-add-open" disabled={full}>
                    {full ? "Máximo 6 productos" : triggerLabel}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {stage === "saved" ? "Producto guardado" : "Añadir un producto"}
                    </DialogTitle>
                    <DialogDescription data-testid="product-progress">
                        {stage === "saved" && reachedMax
                            ? `Has llegado al máximo de ${MAX_PRODUCTS} productos.`
                            : `Producto ${current} de ${MAX_PRODUCTS}`}
                    </DialogDescription>
                </DialogHeader>

                {stage === "capture" && (
                    <div className="space-y-3">
                        <PhotoField
                            label="1. Foto donde se vea el nombre"
                            hint="El frontal del envase, bien enfocado."
                            value={photoFront} onPick={pick(setPhotoFront)} testid="product-photo-front" />
                        <PhotoField
                            label="2. Foto del listado de ingredientes"
                            hint="Suele estar detrás, en un lateral o en el propio frontal (INCI)."
                            value={photoIng} onPick={pick(setPhotoIng)} testid="product-photo-ing" />
                        <p className="text-[12px] text-subtle">Las dos fotos deben ser del mismo producto.</p>
                        <Button onClick={analyze} disabled={analyzing || !photoFront || !photoIng}
                            data-testid="product-analyze" className="w-full">
                            {analyzing ? "Analizando fotos…" : "Analizar fotos"}
                        </Button>
                    </div>
                )}

                {stage === "review" && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-line bg-background p-3">
                            <p className="text-[13px] text-subtle">Esto hemos detectado de tus fotos. Confírmalo o corrígelo abajo.</p>
                            <p className="mt-1 text-[14px] text-ink" data-testid="product-detected">
                                <strong>{form.name || "—"}</strong>{form.brand ? ` · ${form.brand}` : ""}
                            </p>
                            {ingredients.length > 0 && (
                                <p className="mt-1 text-[12px] text-subtle" data-testid="product-detected-ing">
                                    {ingredients.length} ingredientes leídos
                                </p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="pname">Nombre</Label>
                            <Input id="pname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Ej. Crema hidratante" data-testid="product-name" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="pbrand">Marca</Label>
                            <Input id="pbrand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
                                placeholder="Ej. CeraVe" data-testid="product-brand" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="pcat">Tipo de producto</Label>
                            <select id="pcat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                                data-testid="product-category"
                                className="h-10 w-full rounded-lg border border-line bg-card px-3 text-[14px] text-ink outline-none focus:ring-2 focus:ring-ring">
                                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                        </div>
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" onClick={() => setStage("capture")} data-testid="product-back-photos">
                                Volver a las fotos
                            </Button>
                            <Button onClick={save} disabled={saving} data-testid="product-submit" className="flex-1">
                                {saving ? "Guardando…" : "Guardar producto"}
                            </Button>
                        </div>
                    </div>
                )}

                {stage === "saved" && (
                    <div className="space-y-4 py-2 text-center">
                        <p className="text-[15px] font-medium text-ink">Producto guardado ✓</p>
                        {reachedMax ? (
                            <>
                                <p className="text-[13px] text-subtle">Has llegado al máximo de {MAX_PRODUCTS} productos de esta fase.</p>
                                <Button onClick={() => setOpen(false)} data-testid="product-finish" className="w-full">Terminar</Button>
                            </>
                        ) : (
                            <>
                                <p className="text-[13px] text-subtle">Puedes añadir otro producto (hasta {MAX_PRODUCTS}).</p>
                                <Button onClick={resetProduct} data-testid="product-add-another" className="w-full">Añadir otro producto</Button>
                                <Button variant="outline" onClick={() => setOpen(false)} data-testid="product-finish" className="w-full">Terminar por ahora</Button>
                            </>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function Neceser() {
    const navigate = useNavigate();
    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [confirming, setConfirming] = useState(false);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("");
    const pageSize = 12;

    const load = useCallback(async () => {
        setItems(null);
        const { data } = await client.get("/products", { params: { page, page_size: pageSize, q, category } });
        setItems(data.items);
        setTotal(data.total);
    }, [page, q, category]);

    useEffect(() => {
        const t = setTimeout(load, q ? 300 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    const onAdded = () => { setPage(1); load(); };

    const remove = (p) => {
        setItems((prev) => prev.filter((x) => x.id !== p.id));
        setTotal((t) => t - 1);
        const timer = setTimeout(() => client.delete(`/products/${p.id}`).catch(() => {}), 5000);
        toast("Producto eliminado", {
            description: "Puedes recuperarlo durante unos segundos.",
            action: { label: "Deshacer", onClick: () => { clearTimeout(timer); load(); } },
        });
    };

    const pages = Math.ceil(total / pageSize) || 1;

    return (
        <>
            <PageHeader
                title="Mi neceser"
                subtitle="Añade lo que ya tienes. Te decimos qué te encaja, qué sobra y qué no deberías juntar."
                action={<AddDialog onAdded={onAdded} total={total} />}
            />

            <div className="mb-5 flex flex-wrap items-center gap-3">
                <Input placeholder="Buscar producto o marca…" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
                    className="max-w-xs" data-testid="products-search" />
                <select value={category} onChange={(e) => { setPage(1); setCategory(e.target.value); }} data-testid="products-filter"
                    className="h-10 rounded-lg border border-line bg-card px-3 text-[14px] text-ink outline-none focus:ring-2 focus:ring-ring">
                    <option value="">Todos los tipos</option>
                    {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <a href={`${API}/products/export.csv`} className="ml-auto">
                    <Button variant="outline" size="sm" data-testid="products-export">
                        <Download size={15} className="mr-1.5" /> Exportar CSV
                    </Button>
                </a>
            </div>

            {items === null ? (
                <SkeletonList rows={6} />
            ) : items.length === 0 ? (
                <EmptyState testId="products-empty" title="Tu neceser está vacío"
                    text="Añade los productos que ya usas. Con eso podremos decirte qué mantener, qué cambiar y qué te falta."
                    action={<AddDialog onAdded={onAdded} total={total} />} />
            ) : (
                <div className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                        {items.map((p) => {
                            const [sl, sc] = SUIT[p.suitability] || SUIT.adecuado;
                            return (
                                <div key={p.id} className="rounded-lg border border-line bg-card p-4 transition-colors duration-150 hover:border-terracotta/40"
                                    data-testid={`product-card-${p.id}`}>
                                    <div className="flex items-start gap-3">
                                        <Avatar name={p.brand || p.name} size={40} seed={p.id} />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-[15px] font-medium text-ink">{p.name}</p>
                                            <p className="text-[13px] text-subtle">{p.brand || "Sin marca"} · {p.category_label}</p>
                                        </div>
                                        <button onClick={() => remove(p)} data-testid={`product-delete-${p.id}`}
                                            className="rounded-md p-1.5 text-subtle transition-colors duration-150 hover:bg-destructive/10 hover:text-destructive">
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                    <div className="mt-3 flex items-center gap-2">
                                        <span className={`rounded-full border px-2.5 py-0.5 text-[12px] font-medium ${sc}`}>{sl}</span>
                                        <span className="text-[12px] text-subtle">{relativeDate(p.created_at)}</span>
                                    </div>
                                    {p.note && <p className="mt-2 text-[13px] leading-[1.5] text-subtle">{p.note}</p>}
                                </div>
                            );
                        })}
                    </div>

                    {pages > 1 && (
                        <div className="flex items-center justify-between">
                            <span className="text-[13px] text-subtle">Página {page} de {pages}</span>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                                <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</Button>
                            </div>
                        </div>
                    )}

                    {/* Cierre del neceser: acción clara para continuar sin depender del menú lateral */}
                    {total > 0 && (
                        confirming ? (
                            <div className="rounded-lg border border-terracotta/40 bg-terracotta/5 p-6 text-center" data-testid="neceser-confirm">
                                <p className="mx-auto max-w-md text-[16px] leading-[1.6] text-ink">
                                    Ya tenemos lo necesario. Vamos a cruzar lo que nos has contado con los productos que has añadido.
                                </p>
                                <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                                    <Button onClick={() => navigate("/app/resultado")} data-testid="neceser-see-result">Ver mi resultado</Button>
                                    <Button variant="ghost" onClick={() => setConfirming(false)} data-testid="neceser-confirm-back">Volver a mi neceser</Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-wrap items-center justify-center gap-3 border-t border-line pt-6" data-testid="neceser-actions">
                                <AddDialog onAdded={onAdded} total={total} triggerLabel="Añadir otro producto" />
                                <Button variant="outline" onClick={() => setConfirming(true)} data-testid="neceser-done">
                                    Ya he añadido todos mis productos
                                </Button>
                            </div>
                        )
                    )}
                </div>
            )}
        </>
    );
}
